// Helpers for doctor specializations. Different backend endpoints return
// specializations in different shapes, so everything is normalised here.

const toName = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item.trim();
  return (
    item.name ||
    item.specialization_name ||
    item.specialization?.name ||
    item.label ||
    ""
  ).trim();
};

const parseList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return trimmed.split(",");
    }
  }
  return [value];
};

// Returns the doctor's specialization names, primary first, without duplicates
const getSpecializationNames = (doctor) => {
  if (!doctor) return [];

  const items = [
    ...(doctor.primary_specialization ? [{ ...doctor.primary_specialization, is_primary: true }] : []),
    ...parseList(doctor.specializations),
    ...parseList(doctor.merged_specializations),
    ...parseList(doctor.specialite),
    ...parseList(doctor.specialization),
  ];

  const sorted = [...items].sort((a, b) => Boolean(b?.is_primary) - Boolean(a?.is_primary));

  const names = [];
  sorted.forEach((item) => {
    const name = toName(typeof item === "number" ? "" : item);
    // Skip bare ids (some endpoints send the specialization id instead of its name)
    if (name && !/^\d+$/.test(name) && !names.some((n) => n.toLowerCase() === name.toLowerCase())) {
      names.push(name);
    }
  });
  return names;
};

// Keywords used to match a specialty link (home page, footer) against the
// specialization names stored in the backend
const SPECIALTY_KEYWORDS = {
  "Dentist": ["dent", "oral"],
  "General Physician": ["general", "family", "physician"],
  "Cardiologist": ["cardi", "heart"],
  "Nephrologist": ["nephr", "kidney"],
  "Gynecologist": ["gyn", "obstet"],
  "Child Specialist": ["child", "pediat", "paediat"],
  "ENT Specialist": ["ent", "ear", "otolaryng"],
  "Dermatologist": ["derma", "skin"],
  "Eye Specialist": ["eye", "ophthal", "optom"],
  "Dietitian/Nutritionist": ["diet", "nutri"],
};

const matchesSpecialty = (label, specialty) => {
  const keywords = SPECIALTY_KEYWORDS[specialty] || [specialty];
  const text = String(label).toLowerCase();
  return keywords.some((keyword) =>
    new RegExp(`\\b${keyword.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`).test(text)
  );
};

const specialtySearchUrl = (specialty, extra = {}) => {
  const params = new URLSearchParams({ specialty, ...extra });
  return `/search-doctor?${params.toString()}`;
};

export { getSpecializationNames, matchesSpecialty, specialtySearchUrl, SPECIALTY_KEYWORDS };
